import { useContext, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { useAuth0 } from "@auth0/auth0-react";
import { setupAuthInterceptor } from "./api/axiosInstance";
import "./App.css";
import "./css/common.css";
import "./css/wizard.css";
import { MainLayout } from "./components/layouts/MainLayout";
import { IntercomProps, IntercomProvider } from "react-use-intercom";
import { getConnectedUserRole, getIntercomInitPropsForUser } from "./utils";
import VerifyEmail from "./VerifyEmail";
import { IdeasWraper } from "./components/ideas";

import { usePostHog } from "posthog-js/react";
import UnauthorizedMessage from "./UnauthorizedMessage";

import { RoutesProvider, DefaultRouteRedirect } from './routes';
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { AppSkeleton } from "./components/AppSkeleton";
import { useIdeaPages } from "./hooks/useIdeaPages";
import { VALID_APP_ROLES } from './hooks/useUserPermissions';
import { useCurrentUserQuery } from "./hooks/useCurrentUser";
import { getAuthErrorStatus } from "./api/auth";

TimeAgo.addDefaultLocale(en);

const GENERIC_LOGIN_ERROR =
  "We could not sign you in. Please try again, or contact support@getbagel.com.";

const GENERIC_AUTH_ME_ERROR =
  "We could not load your Bagel profile. Please try again, or contact support@getbagel.com.";

// Auth0 surfaces the action's deny() reason as an OAuthError carrying
// error_description; anything else is an unexpected failure we should not print raw.
const getLoginErrorMessage = (error: Error): string => {
  const description = (error as { error_description?: string }).error_description;
  return description || error.message || GENERIC_LOGIN_ERROR;
};

function App() {
  const auth0 = useAuth0();
  setupAuthInterceptor(auth0);
  const posthog = usePostHog();
  const { pages: ideaPages } = useIdeaPages();
  const { routes } = RoutesProvider();
  const {
    data: currentUser,
    isError: currentUserFailed,
    error: currentUserError,
  } = useCurrentUserQuery();
  useEffect(() => {
    if (currentUser) {
      if (posthog && window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN !== 'http://localhost') {
        posthog.identify(currentUser.id, {
          ...currentUser,
          email: currentUser.email,
        });
      }
    }
  }, [currentUser, posthog]);

  // Auth0 denied the login (e.g. the post-login action rejected an unprovisioned
  // user). Drop ?error/?state from the URL so a refresh re-runs the login instead
  // of replaying a consumed callback and reporting a misleading "invalid state".
  useEffect(() => {
    if (auth0.error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [auth0.error]);

  if (
    (auth0.isLoading === undefined && auth0.isAuthenticated === undefined) ||
    auth0.isLoading
  ) {
    return <AppSkeleton />;
  }
  // Must precede the redirect below: a denied user is unauthenticated, so
  // redirecting them back to Auth0 only bounces off the same denial forever.
  if (auth0.error) {
    return <UnauthorizedMessage message={getLoginErrorMessage(auth0.error)} />;
  }
  if (!auth0.isLoading && !auth0.isAuthenticated) {
    // Capture current URL to return after login
    // Can be disabled via REACT_APP_DISABLE_RETURN_TO_URL env variable for rollback
    const disableReturnToUrl = window.__RUNTIME_CONFIG__.REACT_APP_DISABLE_RETURN_TO_URL === 'true';
    
    if (disableReturnToUrl) {
      // Old behavior: don't pass returnTo
      auth0.loginWithRedirect();
    } else {
      // New behavior: capture and pass returnTo
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      auth0.loginWithRedirect({
        appState: { returnTo }
      });
    }
    return <AppSkeleton />;
  } else {
    posthog?.capture("logged_in");
  }

  const forceEmailVerification =
    window.__RUNTIME_CONFIG__.REACT_APP_FORCE_VERIFY_EMAILS === "true";
  if (forceEmailVerification && auth0.user?.email_verified !== true) {
    return <VerifyEmail />;
  }

  if (currentUserFailed) {
    const status = getAuthErrorStatus(currentUserError);
    // Both are terminal: Auth0 would re-mint the same rejected token, so
    // redirecting to login loops. UnauthorizedMessage offers a logout instead.
    return status === 404 || status === 401 ? (
      <UnauthorizedMessage />
    ) : (
      <UnauthorizedMessage message={GENERIC_AUTH_ME_ERROR} />
    );
  }
  // Gate on `data`, not `isLoading`: the query is disabled until
  // `isAuthenticated`, so `isLoading` is false on the render where that flips
  // and the unauthorized screen below would flash before the request goes out.
  if (!currentUser) {
    return <AppSkeleton />;
  }

  const userRole = getConnectedUserRole(currentUser);

  const bagelId = currentUser?.id;
  if (!bagelId || !VALID_APP_ROLES.includes(userRole)) {
    return <UnauthorizedMessage />;
  }

  const intercomInitProps: IntercomProps = getIntercomInitPropsForUser(currentUser);
  
  return (
    <Router>
      <IntercomProvider
        appId={window.__RUNTIME_CONFIG__.REACT_APP_INTERCOM_APP_ID}
        autoBoot={true}
        autoBootProps={intercomInitProps}
      >
        <MainLayout>
          <Routes>
            {routes.map((route: any) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              >
                {route.children?.map((childRoute: any) => (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={childRoute.element}
                  />
                ))}
              </Route>
            ))}
            {ideaPages?.map((route: any) => (
              <Route
                key={route.key || route.route}
                path={`/ideas/${route.key || route.route}`}
                element={<IdeasWraper pageKey={route.key} />}
              />
            ))}
            {/* Catch-all route for /ideas/:key - will handle invalid keys and redirect */}
            <Route
              path="/ideas/:key"
              element={
                <RoleProtectedRoute allowedRoles={['manager', 'admin']}>
                  <IdeasWraper pageKey={null} />
                </RoleProtectedRoute>
              }
            />
            {/* If URL does not exist: agent/GTM → customer360, else → /ideas */}
            <Route path="*" element={<DefaultRouteRedirect />} />
          </Routes>
        </MainLayout>
      </IntercomProvider>
    </Router>
  );
}

export default App;
